import { Box, Text, Group, Button, Badge, SimpleGrid } from '@mantine/core'
import { format } from 'date-fns'
import { FirearmPartDto, FirearmPartFormValues } from '../../app/models/firearm'

const CONDITION_COLORS: Record<string, string> = {
  New: 'green',
  Excellent: 'green',
  Good: 'blue',
  Fair: 'amber',
  Worn: 'orange',
  'Needs Replacement': 'red'
}

function formatCurrency (value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

function getConditionColor (condition: string | null | undefined): string {
  if (!condition) return 'gray'
  return CONDITION_COLORS[condition] ?? 'gray'
}

interface PartItem {
  id?: string;
  name: string;
  description?: string | null;
  partNumber?: string | null;
  manufacturer?: string | null;
  value: number;
  condition?: string | null;
  installDate?: string | null;
  notes?: string | null;
}

interface Props {
  parts: PartItem[];
  totalValue?: number;
  editable?: boolean;
  onAdd?: () => void;
  onAddFromBin?: () => void;
  onEdit?: (part: FirearmPartFormValues, index: number) => void;
  onDelete?: (index: number) => void;
}

export default function PartsDisplay ({ parts, totalValue, editable, onAdd, onAddFromBin, onEdit, onDelete }: Props) {
  if (parts.length === 0 && !editable) {
    return null
  }

  if (parts.length === 0) {
    return (
      <Box>
        <Text size="md" color="dimmed" align="center" py="xl">
          No parts added yet. Track scopes, magazines, and accessories to monitor total value.
        </Text>
        {editable && (onAdd || onAddFromBin) && (
          <Group spacing="xs">
            {onAdd && (
              <Button onClick={onAdd} variant="light" color="gray">
                Add part
              </Button>
            )}
            {onAddFromBin && (
              <Button onClick={onAddFromBin} variant="light" color="gray">
                Add from parts bin
              </Button>
            )}
          </Group>
        )}
      </Box>
    )
  }

  return (
    <Box>
      {editable && (onAdd || onAddFromBin) && (
        <Group position="apart" mb="md">
          <Text weight={600} size="sm" color="dimmed">
            Custom parts ({parts.length})
          </Text>
          <Group spacing="xs">
            {onAdd && (
              <Button size="xs" variant="filled" onClick={onAdd}>
                Add part
              </Button>
            )}
            {onAddFromBin && (
              <Button size="xs" variant="light" onClick={onAddFromBin}>
                Add from parts bin
              </Button>
            )}
          </Group>
        </Group>
      )}
      <SimpleGrid cols={1} breakpoints={[{ minWidth: 480, cols: 2 }, { minWidth: 768, cols: 3 }]} spacing="md">
        {parts.map((part, index) => (
          <Box
            key={part.id ?? `new-${index}`}
            sx={{
              padding: 16,
              borderRadius: 8,
              background: 'var(--gs-surface)',
              border: '1px solid var(--gs-border)',
              transition: 'border-color 150ms',
              '&:hover': { borderColor: 'var(--gs-border-bright)' }
            }}
          >
            <Group position="apart" mb={8}>
              <Text weight={600} size="md" lineClamp={1}>
                {part.name}
              </Text>
              {part.condition && (
                <Badge
                  size="sm"
                  color={getConditionColor(part.condition)}
                  variant="light"
                  radius="xl"
                >
                  {part.condition}
                </Badge>
              )}
            </Group>
            {(part.partNumber || part.manufacturer) && (
              <Text size="xs" color="dimmed" mb={4}>
                {[part.manufacturer, part.partNumber].filter(Boolean).join(' · ')}
              </Text>
            )}
            <Text weight={600} size="lg" mb={8} sx={{ color: 'var(--gs-gold)', fontVariantNumeric: 'tabular-nums' }}>
              {formatCurrency(part.value)}
            </Text>
            {part.description && (
              <Text size="sm" color="dimmed" lineClamp={2} mb={4}>
                {part.description}
              </Text>
            )}
            {part.installDate && (
              <Text size="xs" color="dimmed">
                Installed {format(new Date(part.installDate), 'MMM d, yyyy')}
              </Text>
            )}
            {editable && (onEdit || onDelete) && (
              <Group spacing="xs" mt="md" sx={{ borderTop: '1px solid var(--gs-border)', paddingTop: 12 }}>
                {onEdit && (
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={() =>
                      onEdit(
                        {
                          id: (part as FirearmPartDto).id,
                          name: part.name,
                          description: part.description ?? '',
                          partNumber: part.partNumber ?? '',
                          manufacturer: part.manufacturer ?? '',
                          value: part.value,
                          condition: part.condition ?? '',
                          installDate: part.installDate ?? '',
                          notes: part.notes ?? ''
                        },
                        index
                      )
                    }
                  >
                    Edit
                  </Button>
                )}
                {onDelete && (
                  <Button size="xs" variant="subtle" color="red" onClick={() => onDelete(index)}>
                    Remove
                  </Button>
                )}
              </Group>
            )}
          </Box>
        ))}
      </SimpleGrid>
      {totalValue !== undefined && totalValue > 0 && (
        <Box
          mt="md"
          py="sm"
          px="md"
          sx={{
            borderRadius: 8,
            backgroundColor: 'rgba(0,0,0,.03)',
            border: '1px solid var(--gs-border)'
          }}
        >
          <Group position="apart">
            <Text size="sm" weight={600} color="dimmed">
              Total parts value
            </Text>
            <Text weight={700} size="lg" sx={{ color: 'var(--gs-gold)', fontVariantNumeric: 'tabular-nums' }}>
              {formatCurrency(totalValue)}
            </Text>
          </Group>
        </Box>
      )}
    </Box>
  )
}
